import { useMemo } from 'react';
import { SuiFrenImage } from '@suigar/suifrens-display-system';
import type { SuiFrenAttributes } from '@suigar/suifrens-display-system';
import type { AccessoryMetadata } from '@suigar/suifrens-display-system';
import { cn } from '@/utils';
import { getDeterministicSuiFren } from '@/utils/deterministicAppearance';
import { isBullsharkAttributes } from './ReactiveSuiFrenImage';

const sizeClasses = {
	xs: 'h-9 w-9',
	sm: 'h-12 w-12',
	md: 'h-14 w-14',
	lg: 'h-16 w-16',
	xl: 'h-20 w-20',
	custom: '',
} as const;

type SizeKey = keyof typeof sizeClasses;
type PortraitFrame = 'circle' | 'rounded' | 'none';

const mergeAccessories = (base?: AccessoryMetadata[], overrides?: AccessoryMetadata[]) => {
	if (!base?.length && !overrides?.length) {
		return undefined;
	}

	const byType = new Map<string, AccessoryMetadata>();
	base?.forEach((item) => byType.set(item.type, item));
	overrides?.forEach((item) => byType.set(item.type, item));
	return Array.from(byType.values());
};

export interface SuiFrenPortraitProps {
	attributes?: SuiFrenAttributes;
	accessories?: AccessoryMetadata[];
	seed?: string | null;
	deterministicVariant?: 'auto' | 'capy' | 'bullshark';
	className?: string;
	size?: SizeKey;
	flipHorizontal?: boolean;
	zoom?: number;
	focusOffset?: {
		x?: number;
		y?: number;
	};
	showShadow?: boolean;
	frame?: PortraitFrame;
}

export function SuiFrenPortrait({
	attributes,
	accessories,
	seed,
	deterministicVariant = 'auto',
	className,
	size = 'md',
	flipHorizontal = false,
	zoom = 1.3,
	focusOffset,
	showShadow = false,
	frame = 'circle',
}: SuiFrenPortraitProps) {
	const deterministicAppearance = useMemo(() => {
		const normalizedSeed = seed?.trim();
		if (!normalizedSeed) {
			return null;
		}
		return getDeterministicSuiFren(normalizedSeed, { variant: deterministicVariant });
	}, [seed, deterministicVariant]);

	const resolvedAttributes = useMemo(() => {
		if (deterministicAppearance) {
			return {
				...deterministicAppearance.attributes,
				...(attributes ?? {}),
			} as SuiFrenAttributes;
		}
		return attributes ?? null;
	}, [attributes, deterministicAppearance]);

	const resolvedAccessories = useMemo(
		() => mergeAccessories(deterministicAppearance?.accessories, accessories),
		[accessories, deterministicAppearance],
	);

	if (!resolvedAttributes) {
		return null;
	}

	const { x: offsetX = 0, y: offsetY = -12 } = focusOffset ?? {};
	const frameClasses: Record<PortraitFrame, string> = {
		circle: 'rounded-full bg-card/40',
		rounded: 'rounded-2xl bg-card/30',
		none: 'rounded-none bg-transparent',
	};

	const transforms = [];

	if (flipHorizontal) {
		transforms.push('scaleX(-1)');
	}

	transforms.push(`translate(${offsetX}%, ${offsetY}%)`);
	transforms.push(`scale(${zoom})`);

	const transform = transforms.join(' ');

	return (
		<div className={cn('relative shrink-0 overflow-hidden', sizeClasses[size], frameClasses[frame], className)}>
			<div
				className="absolute inset-0 flex items-center justify-center"
				style={{
					transform,
					transformOrigin: isBullsharkAttributes(resolvedAttributes) ? '42% -24%' : '62% -24%',
				}}
			>
				<div className="h-full w-full">
					<SuiFrenImage attributes={resolvedAttributes} accessories={resolvedAccessories} shadow={showShadow} />
				</div>
			</div>
		</div>
	);
}
